import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import ThemeToggle from "./ui/theme-toggle";
import {
  LayoutDashboard,
  Users,
  FileText,
  Calendar,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronLeft,
  Receipt,
  Upload,
  Settings,
  Database,
  PenTool,
  Mail,
  FileSpreadsheet,
  Sparkles, AlertTriangle, History, FolderInput, ClipboardCheck} from "lucide-react";
import { toast } from "sonner";
import NotificationsDropdown from "./NotificationsDropdown";
import GlobalSearch from "./GlobalSearch";
import InstallApp from "./InstallApp";

const menuGroups = [
  {
    titolo: "Operativita'",
    voci: [
      { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
      { icon: Users, label: "Clienti", path: "/clients" },
      { icon: FileText, label: "Polizze", path: "/policies" },
      { icon: Calendar, label: "Calendario", path: "/calendar" },
      { icon: AlertTriangle, label: "Da gestire", path: "/operations" },
      { icon: FolderInput, label: "Documenti da assegnare", path: "/documenti-da-assegnare" },
      { icon: ClipboardCheck, label: "Polizze da confermare", path: "/polizze-da-confermare" },
    ],
  },
  {
    titolo: "Controllo",
    voci: [
      { icon: Receipt, label: "Report", path: "/reports" },
      { icon: FileSpreadsheet, label: "Portafoglio", path: "/portfolio" },
      { icon: History, label: "Recupero scadenze", path: "/recupero-scadenze" },
    ],
  },
  {
    titolo: "Strumenti",
    voci: [
      { icon: Upload, label: "Import Dati", path: "/import" },
      { icon: PenTool, label: "Firma Digitale", path: "/signatures" },
      { icon: Mail, label: "Email", path: "/email" },
      { icon: Sparkles, label: "Sara AI", path: "/assistant" },
      { icon: Database, label: "Backup", path: "/backup" },
      { icon: Settings, label: "Impostazioni", path: "/general-settings" },
    ],
  },
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Il menu mobile deve chiudersi cambiando pagina da qualunque punto (per
  // esempio dai risultati della ricerca globale, che sta nell'intestazione),
  // non solo toccando una voce del menu: altrimenti resta aperto e copre la
  // pagina appena aperta.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
      navigate('/login');
    } catch (error) {
      toast.error("Errore durante il logout");
    }
  };

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path || 
      (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
    return (
      <button
        onClick={() => {
          navigate(item.path);
          setMobileMenuOpen(false);
        }}
        data-testid={`nav-${item.path.slice(1)}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-150 group ${
          isActive
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <item.icon className={`h-[18px] w-[18px] flex-shrink-0 transition-transform duration-150 ${!isActive ? 'group-hover:scale-110' : ''}`} strokeWidth={isActive ? 2.2 : 1.8} />
        {(sidebarOpen || mobileMenuOpen) && (
          <span className="text-sm font-medium truncate">{item.label}</span>
        )}
      </button>
    );
  };

  // Le voci sono raggruppate per tipo di lavoro: prima si usano (clienti,
  // polizze, scadenze), poi si controllano i numeri, poi gli strumenti.
  const VociMenu = ({ mostraTitoli }) => (
    <>
      {menuGroups.map((gruppo) => (
        <div key={gruppo.titolo} className="pt-3 first:pt-0">
          {mostraTitoli && (
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {gruppo.titolo}
            </p>
          )}
          <div className="space-y-0.5">
            {gruppo.voci.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Collegamento per chi naviga da tastiera: porta subito al contenuto
          senza attraversare tutto il menu a ogni cambio pagina. */}
      <a
        href="#contenuto-principale"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:text-sm focus:font-medium"
      >
        Salta al contenuto
      </a>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-sidebar border-r border-border transition-all duration-300 relative ${
          sidebarOpen ? 'w-60' : 'w-[68px]'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary rounded-md flex items-center justify-center flex-shrink-0 shadow-sm">
              <Shield className="w-5 h-5 text-primary-foreground" strokeWidth={2.2} />
            </div>
            {sidebarOpen && (
              <div className="animate-slideIn">
                <h1 className="text-base font-bold font-heading text-foreground leading-tight">InsureAssist</h1>
                <p className="text-[11px] text-muted-foreground leading-tight">Broker Dashboard</p>
              </div>
            )}
          </div>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-[72px] -right-3 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center hover:bg-muted transition-colors duration-150 z-10 shadow-sm"
        >
          <ChevronLeft className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-300 ${!sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-3">
          <nav className="px-3">
            <VociMenu mostraTitoli={sidebarOpen} />
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-3 border-t border-border">
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className={`w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-150 ${!sidebarOpen ? 'px-3' : ''}`}
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.8} />
            {sidebarOpen && <span className="text-sm">Esci</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center shadow-sm">
              <Shield className="w-4 h-4 text-primary-foreground" strokeWidth={2.2} />
            </div>
            <h1 className="text-base font-bold font-heading text-foreground">InsureAssist</h1>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <NotificationsDropdown />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="h-9 w-9"
             aria-label="Apri o chiudi il menu">
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        <div className="mt-2.5">
          <GlobalSearch />
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="absolute top-[88px] left-0 right-0 bg-card border-b border-border p-3 shadow-lg max-h-[calc(100vh-100px)] overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <nav>
              <VociMenu mostraTitoli={true} />
            </nav>
            <div className="mt-3 pt-3 border-t border-border">
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
              >
                <LogOut className="h-[18px] w-[18px]" />
                <span className="text-sm">Esci</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between gap-4 h-16 px-8 border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-30">
          <GlobalSearch />
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <NotificationsDropdown />
          </div>
        </div>
        <div id="contenuto-principale" className="p-6 lg:px-8 lg:py-8 pt-28 lg:pt-8">
          {children}
        </div>
      </main>
      <InstallApp />
    </div>
  );
}
